/**
 * PortfolioTable Component
 *
 * Main orchestrator component that composes all portfolio table sub-components.
 * Receives data as props (stateless regarding data fetching) and manages internal UI state.
 *
 * Responsibilities:
 * - Column resolution from config
 * - Search and column filtering
 * - Sorting
 * - Currency grouping
 * - CSV export
 * - Loading and error states
 * - Composing sub-components
 */

import { RefreshCw, Search } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { IbkrAccountSummary } from "../../types";
import AccountSummaryBar from "./AccountSummaryBar";
import GroupRow from "./GroupRow";
import TableControls, { GroupByOption } from "./TableControls";
import TableHeader from "./TableHeader";
import TableRow, { StageOption } from "./TableRow";
import { getColumnDefinitions } from "./columns/columnDefinitions";
import { ColumnDefinition, ColumnFilters, PortfolioRow, SortConfig } from "./columns/columnTypes";
import { PortfolioTableConfig } from "./configs/types";

// ============================================================================
// Types
// ============================================================================

export interface PortfolioTableProps {
  /** Table configuration (columns, features, etc.) */
  config: PortfolioTableConfig;
  /** Portfolio rows to display */
  rows: PortfolioRow[];
  /** Account summary data */
  accountSummary: IbkrAccountSummary | null;
  /** Whether data is loading */
  isLoading: boolean;
  /** Whether rows have partial data (e.g., USD values still loading) */
  isPartialData?: boolean;
  /** Error if data fetch failed */
  error: Error | null;
  /** Timestamp of last data update */
  lastUpdatedAt: number;
  /** Callback to refresh data */
  onRefresh: () => void | Promise<void>;
  /** Optional: Cancel order callback for execution view (stageId, orderId) */
  onCancelOrder?: (stageId: number, orderId: string) => Promise<void>;
  /** Optional: Refresh order status callback (stageId, orderId) */
  onRefreshOrderStatus?: (stageId: number, orderId: string) => Promise<void>;
  /** Optional: Fetch trades callback (stageId, orderId) */
  onFetchTrades?: (stageId: number, orderId: string) => Promise<void>;
  // === Phase 2: Interactive editing props ===
  /** Available stages for dropdown selection */
  stageOptions?: StageOption[];
  /** Callback when stage assignment changes */
  onStageChange?: (conId: number, stageId: number | null) => void;
  /** Callback when trade qty is edited */
  onTradeQtyChange?: (conId: number, newQty: number) => void;
  /** Set of conIds that have unsaved local changes */
  dirtyRowConIds?: Set<number>;
  /** Optional classes for the table scroll container */
  tableContainerClassName?: string;
  /** Optional slot rendered between the controls bar and the table */
  belowControlsSlot?: React.ReactNode;
}

interface GroupedRows {
  [key: string]: {
    rows: PortfolioRow[];
    totals: {
      mktValueUsd: number;
      unrealizedPnlUsd: number;
      count: number;
    };
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format relative time from timestamp
 */
function getRelativeTime(timestamp: number): string {
  if (!timestamp) return "Never";
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);

  if (diffSec < 60) return "Just now";
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;
  if (diffHr === 1) return "1 hour ago";
  return `${diffHr} hours ago`;
}

/**
 * Escape a value for CSV export
 */
function escapeCsvValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const strValue = String(value);
  if (strValue.includes(",") || strValue.includes('"') || strValue.includes("\n")) {
    return `"${strValue.replace(/"/g, '""')}"`;
  }
  return strValue;
}

// ============================================================================
// Component
// ============================================================================

const PortfolioTable: React.FC<PortfolioTableProps> = ({
  config,
  rows,
  accountSummary,
  isLoading,
  isPartialData = false,
  error,
  lastUpdatedAt,
  onRefresh,
  onCancelOrder,
  onRefreshOrderStatus,
  onFetchTrades,
  // Phase 2: Interactive editing props
  stageOptions,
  onStageChange,
  onTradeQtyChange,
  dirtyRowConIds,
  tableContainerClassName,
  belowControlsSlot,
}) => {
  const { isDarkMode } = useTheme();

  // ============================================================================
  // Internal State
  // ============================================================================

  // Compute initial column filters from config.defaultFilters
  const initialFilters = useMemo((): ColumnFilters => {
    const filters: ColumnFilters = {};
    if (config.defaultFilters) {
      Object.entries(config.defaultFilters).forEach(([columnId, spec]) => {
        filters[columnId] = new Set(spec.values);
      });
    }
    return filters;
  }, [config.defaultFilters]);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(initialFilters);
  const [groupBy, setGroupBy] = useState<GroupByOption>("none");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedColumnsActive, setExpandedColumnsActive] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[] | null>(null);
  const [mobileFiltersVisible, setMobileFiltersVisible] = useState(false);

  // Callback for when TableHeader changes visible columns (group collapse/expand)
  const handleVisibleColumnsChange = useCallback((ids: string[]) => {
    setVisibleColumnIds(ids);
  }, []);

  // Reset filters when config changes (e.g., switching between views)
  useEffect(() => {
    setColumnFilters(initialFilters);
  }, [initialFilters]);

  // Compute whether there are any active user-applied filters
  const hasActiveFilters = useMemo((): boolean => {
    // Check if search is active
    if (searchQuery.trim()) return true;

    // Check if any column filter differs from initial filters
    for (const [columnId, selectedValues] of Object.entries(columnFilters)) {
      const initialSet = initialFilters[columnId] ?? new Set();
      if (selectedValues.size !== initialSet.size) return true;
      for (const v of selectedValues) {
        if (!initialSet.has(v)) return true;
      }
    }

    return false;
  }, [searchQuery, columnFilters, initialFilters]);

  // Handler to clear all user-applied filters
  const handleClearAllFilters = useCallback(() => {
    setSearchQuery("");
    setColumnFilters(initialFilters);
  }, [initialFilters]);

  // ============================================================================
  // Active Columns (base + expanded when toggled)
  // ============================================================================

  const activeColumns = useMemo((): ColumnDefinition[] => {
    const base = [...config.columns];
    const expanded = expandedColumnsActive ? config.expandedColumns ?? [] : [];
    
    // Helper to insert after a specific column
    const insertAfter = (targetId: string, insertId: string) => {
      const index = base.indexOf(targetId);
      if (index !== -1) {
        base.splice(index + 1, 0, insertId);
      } else {
        // Fallback: try to insert after the column that usually precedes the target
        // (Not perfect, but handles most cases)
        if (targetId === "excluded" && base.includes("conId")) {
             const i = base.indexOf("conId");
             base.splice(i + 1, 0, insertId);
        } else {
             base.push(insertId);
        }
      }
    };

    // Insert expanded columns at their logical positions
    // We process them in a specific order to respect dependencies (e.g. secType after excluded)
    // defined by the original rebalanceConfig order
    const orderedExpanded = [
        // Symbol Info group
        "excluded", 
        "secType", 
        "currency",
        
        // Current Position group
        "mktValue",
        "mktValueUsd",
        
        // Stage Assignment group
        "stageStatus",
        
        // Catch-all for others
        ...expanded.filter(id => !["excluded", "secType", "currency", "mktValue", "mktValueUsd", "stageStatus"].includes(id))
    ];

    orderedExpanded.forEach(id => {
      if (expanded.includes(id) && !base.includes(id)) {
        if (id === "excluded") {
            insertAfter("conId", id);
        } else if (id === "secType") {
            // Try inserting after excluded first, then conId
            if (base.includes("excluded")) insertAfter("excluded", id);
            else insertAfter("conId", id);
        } else if (id === "currency") {
            // Try inserting after secType, then excluded, then conId
            if (base.includes("secType")) insertAfter("secType", id);
            else if (base.includes("excluded")) insertAfter("excluded", id);
            else insertAfter("conId", id);
        } else if (id === "mktValue") {
            insertAfter("priceUsd", id);
        } else if (id === "mktValueUsd") {
            if (base.includes("mktValue")) insertAfter("mktValue", id);
            else insertAfter("priceUsd", id);
        } else if (id === "stageStatus") {
            insertAfter("drift", id);
        } else {
            // Default append
            base.push(id);
        }
      }
    });
    
    return getColumnDefinitions(base);
  }, [config.columns, config.expandedColumns, expandedColumnsActive]);

  // Columns to display in rows (filtered by collapsed groups)
  const displayColumns = useMemo((): ColumnDefinition[] => {
    if (!visibleColumnIds) return activeColumns;
    return activeColumns.filter(col => visibleColumnIds.includes(col.id));
  }, [activeColumns, visibleColumnIds]);

  // ============================================================================
  // Filter Options (extracted from all rows, not filtered rows)
  // ============================================================================

  const filterOptions = useMemo((): Record<string, string[]> => {
    const options: Record<string, string[]> = {};
    activeColumns.forEach(col => {
      if (col.filterable) {
        if (col.filterMode === "nonzero") {
          // For nonzero mode, provide a single "Non-zero" option
          options[col.id] = ["Non-zero"];
        } else if (col.filterOptions) {
          // Discrete mode: use filterOptions function
          options[col.id] = col.filterOptions(rows);
        }
      }
    });
    return options;
  }, [activeColumns, rows]);

  // ============================================================================
  // Filtering Logic
  // ============================================================================

  const filteredRows = useMemo((): PortfolioRow[] => {
    let result = rows;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row => {
        return (
          row.symbol.toLowerCase().includes(query) ||
          row.currency.toLowerCase().includes(query) ||
          (row.group?.toLowerCase().includes(query) ?? false) ||
          (row.sector?.toLowerCase().includes(query) ?? false) ||
          (row.secType?.toLowerCase().includes(query) ?? false) ||
          String(row.conId).includes(query)
        );
      });
    }

    // Apply column filters
    Object.entries(columnFilters).forEach(([columnId, selectedValues]) => {
      if (selectedValues.size > 0) {
        const column = activeColumns.find(c => c.id === columnId);
        if (!column) return;

        result = result.filter(row => {
          const value = column.accessor(row);

          // Handle nonzero filter mode
          if (column.filterMode === "nonzero") {
            // "Non-zero" selected means show only non-zero, non-null values
            if (selectedValues.has("Non-zero")) {
              const numValue = Number(value);
              return value !== null && value !== undefined && !isNaN(numValue) && numValue !== 0;
            }
            return true;
          }

          // Discrete mode: check if value is in selected set
          return selectedValues.has(String(value ?? ""));
        });
      }
    });

    return result;
  }, [rows, searchQuery, columnFilters, activeColumns]);

  // ============================================================================
  // Sorting Logic
  // ============================================================================

  const sortedRows = useMemo((): PortfolioRow[] => {
    if (!sortConfig) return filteredRows;

    const column = activeColumns.find(c => c.id === sortConfig.column);
    if (!column) return filteredRows;

    const accessor = column.sortAccessor ?? column.accessor;

    return [...filteredRows].sort((a, b) => {
      const aVal = accessor(a);
      const bVal = accessor(b);

      // Handle nulls
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Compare
      let comparison = 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredRows, sortConfig, activeColumns]);

  // ============================================================================
  // Grouping Logic
  // ============================================================================

  /**
   * Get the grouping key for a row based on the current groupBy option.
   */
  const getGroupKey = useCallback(
    (row: PortfolioRow): string => {
      switch (groupBy) {
        case "currency":
          return row.currency || "Unknown";
        case "sector":
          return row.sector || "Unknown";
        case "group":
          return row.group || "Unknown";
        case "secType":
          return row.secType || "Unknown";
        case "timezone":
          return row.timeZoneId || "Unknown";
        default:
          return "Unknown";
      }
    },
    [groupBy]
  );

  const groupedData = useMemo((): GroupedRows | null => {
    if (groupBy === "none") return null;

    const groups: GroupedRows = {};
    sortedRows.forEach(row => {
      const key = getGroupKey(row);
      if (!groups[key]) {
        groups[key] = {
          rows: [],
          totals: { mktValueUsd: 0, unrealizedPnlUsd: 0, count: 0 },
        };
      }
      groups[key].rows.push(row);
      groups[key].totals.mktValueUsd += row.mktValueUsd ?? 0;
      groups[key].totals.unrealizedPnlUsd += row.unrealizedPnlUsd ?? 0;
      groups[key].totals.count += 1;
    });

    return groups;
  }, [sortedRows, groupBy, getGroupKey]);

  // Check if all groups are expanded
  const allGroupsExpanded = useMemo(() => {
    if (!groupedData) return false;
    const groupKeys = Object.keys(groupedData);
    return groupKeys.length > 0 && groupKeys.every(key => expandedGroups.has(key));
  }, [groupedData, expandedGroups]);

  // ============================================================================
  // Event Handlers
  // ============================================================================

  const handleSort = useCallback((columnId: string) => {
    setSortConfig(prev => {
      if (prev?.column === columnId) {
        return prev.direction === "asc" ? { column: columnId, direction: "desc" } : null;
      }
      return { column: columnId, direction: "asc" };
    });
  }, []);

  const handleToggleFilter = useCallback((columnId: string, value: string) => {
    setColumnFilters(prev => {
      const current = prev[columnId] ?? new Set();
      const updated = new Set(current);
      if (updated.has(value)) {
        updated.delete(value);
      } else {
        updated.add(value);
      }
      return { ...prev, [columnId]: updated };
    });
  }, []);

  const handleSelectAllFilter = useCallback((columnId: string, values: string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: new Set(values),
    }));
  }, []);

  const handleClearFilter = useCallback((columnId: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: new Set(),
    }));
  }, []);

  const handleToggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups(prev => {
      const updated = new Set(prev);
      if (updated.has(groupKey)) {
        updated.delete(groupKey);
      } else {
        updated.add(groupKey);
      }
      return updated;
    });
  }, []);

  const handleExpandAllGroups = useCallback(() => {
    if (!groupedData) return;
    const groupKeys = Object.keys(groupedData);
    if (allGroupsExpanded) {
      setExpandedGroups(new Set());
    } else {
      setExpandedGroups(new Set(groupKeys));
    }
  }, [groupedData, allGroupsExpanded]);

  // ============================================================================
  // CSV Export
  // ============================================================================

  const handleExportCsv = useCallback(() => {
    let csvContent = "";

    // Header section
    csvContent += "PORTFOLIO EXPORT\n";
    csvContent += `Export Date,${new Date().toISOString()}\n`;
    csvContent += `Total Positions,${sortedRows.length}\n`;

    // Account summary if available
    if (accountSummary) {
      csvContent += "\nACCOUNT SUMMARY\n";
      csvContent += `Account,${escapeCsvValue(accountSummary.accountCode)}\n`;
      csvContent += `Net Liquidation,$${(Number(accountSummary.netLiquidationSecurity) || 0).toFixed(2)}\n`;
      csvContent += `Cash Balance,$${(Number(accountSummary.totalCashValueSecurity) || 0).toFixed(2)}\n`;
      csvContent += `Gross Position,$${(Number(accountSummary.grossPositionValueSecurity) || 0).toFixed(2)}\n`;
      csvContent += `Buying Power,$${(Number(accountSummary.buyingPower) || 0).toFixed(2)}\n`;
    }

    // Positions header
    csvContent += "\nPOSITIONS\n";
    const headers = activeColumns.map(col => col.label);
    csvContent += headers.join(",") + "\n";

    // Position rows
    sortedRows.forEach(row => {
      const rowValues = activeColumns.map(col => {
        const value = col.accessor(row);
        if (typeof value === "number") {
          return value.toFixed(2);
        }
        return escapeCsvValue(value);
      });
      csvContent += rowValues.join(",") + "\n";
    });

    // Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `portfolio_${new Date().toISOString().slice(0, 10)}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [sortedRows, activeColumns, accountSummary]);

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderTableBody = () => {
    if (groupedData) {
      // Grouped rendering
      const groupKeys = Object.keys(groupedData).sort();
      return (
        <tbody>
          {groupKeys.map(groupKey => {
            const group = groupedData[groupKey];
            const isExpanded = expandedGroups.has(groupKey);
            return (
              <React.Fragment key={groupKey}>
                <GroupRow
                  groupKey={groupKey}
                  groupBy={groupBy}
                  totals={group.totals}
                  isExpanded={isExpanded}
                  onToggle={() => handleToggleGroup(groupKey)}
                  columnCount={activeColumns.length}
                  isDarkMode={isDarkMode}
                />
                {isExpanded &&
                  group.rows.map(row => (
                    <TableRow
                      key={row.conId}
                      row={row}
                      columns={displayColumns}
                      isDarkMode={isDarkMode}
                      isGrouped
                      onCancelOrder={onCancelOrder}
                      onRefreshOrderStatus={onRefreshOrderStatus}
                      onFetchTrades={onFetchTrades}
                      stageOptions={stageOptions}
                      onStageChange={onStageChange}
                      onTradeQtyChange={onTradeQtyChange}
                      isDirty={dirtyRowConIds?.has(row.conId) ?? false}
                      columnGroups={config.columnGroups}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      );
    }

    // Flat rendering
    return (
      <tbody>
        {sortedRows.map(row => (
          <TableRow
            key={row.conId}
            row={row}
            columns={displayColumns}
            isDarkMode={isDarkMode}
            onCancelOrder={onCancelOrder}
            onRefreshOrderStatus={onRefreshOrderStatus}
            onFetchTrades={onFetchTrades}
            stageOptions={stageOptions}
            onStageChange={onStageChange}
            onTradeQtyChange={onTradeQtyChange}
            isDirty={dirtyRowConIds?.has(row.conId) ?? false}
            columnGroups={config.columnGroups}
          />
        ))}
      </tbody>
    );
  };

  const renderLoadingSkeleton = () => (
    <tbody>
      {Array.from({ length: 10 }).map((_, i) => (
        <tr key={i}>
          {displayColumns.map(col => (
            <td key={col.id} className={`px-2 py-3 border ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <div className={`h-4 rounded animate-pulse ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );

  const renderEmptyState = () => (
    <tbody>
      <tr>
        <td
          colSpan={displayColumns.length}
          className={`px-4 py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {searchQuery || Object.values(columnFilters).some(s => s.size > 0)
            ? "No positions match your filters"
            : "No positions to display"}
        </td>
      </tr>
    </tbody>
  );

  const renderErrorState = () => (
    <div
      className={`mx-3 p-4 rounded border ${
        isDarkMode ? "bg-red-900/20 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-600"
      }`}
    >
      <p className="font-medium">Error loading portfolio data</p>
      <p className="text-sm mt-1">{error?.message || "An unexpected error occurred"}</p>
      <button
        onClick={() => { Promise.resolve(onRefresh()).catch(() => {}); }}
        className={`mt-2 px-3 py-1 text-sm rounded border ${
          isDarkMode ? "border-red-700 hover:bg-red-900/30" : "border-red-300 hover:bg-red-100"
        }`}
      >
        Retry
      </button>
    </div>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {belowControlsSlot && (
          <div className="flex items-center justify-between px-3 py-2">
            {belowControlsSlot}
          </div>
        )}
        {renderErrorState()}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Account Summary Bar */}
      {config.dataRequirements.accountSummary && (
        <div className="flex-shrink-0">
          <AccountSummaryBar
            accountSummary={accountSummary}
            positionCount={sortedRows.length}
            isExpanded={summaryExpanded}
            onToggleExpand={() => setSummaryExpanded(p => !p)}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {/* Table Controls */}
      <div className="flex-shrink-0">
        <TableControls
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onExport={handleExportCsv}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          expandedColumnsActive={expandedColumnsActive}
          onToggleExpandedColumns={() => setExpandedColumnsActive(p => !p)}
          onExpandAllGroups={handleExpandAllGroups}
          allGroupsExpanded={allGroupsExpanded}
          isLoading={isLoading}
          hasData={rows.length > 0}
          isDarkMode={isDarkMode}
          features={config.features}
          hasTimezoneData={rows.some(row => row.timeZoneId !== null)}
          hasActiveFilters={hasActiveFilters}
          onClearAllFilters={handleClearAllFilters}
          mobileVisible={mobileFiltersVisible}
        />
      </div>

      {/* Info bar: position count + updated time */}
      <div
        className={`flex items-center justify-between px-3 py-2 text-xs ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        <div className="flex items-center gap-2">
          {/* Mobile filter toggle button */}
          <button
            onClick={() => setMobileFiltersVisible(v => !v)}
            className={`sm:hidden p-1.5 rounded transition-colors ${
              mobileFiltersVisible
                ? isDarkMode
                  ? "bg-teal-900/50 text-teal-300"
                  : "bg-teal-100 text-teal-700"
                : isDarkMode
                ? "text-gray-400 hover:text-teal-400"
                : "text-gray-500 hover:text-teal-600"
            }`}
            title={mobileFiltersVisible ? "Hide filters" : "Show filters"}
          >
            <Search className="w-4 h-4" />
            {(hasActiveFilters || searchQuery) && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-teal-500 rounded-full" />
            )}
          </button>
          <span>
            {filteredRows.length} of {rows.length} positions
          </span>
        </div>
        <div className="flex items-center gap-3">
          {isPartialData && (
            <span className="flex items-center gap-1 text-amber-500 text-xs">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Updating rates...
            </span>
          )}
          {lastUpdatedAt > 0 && (
            <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
              Updated {getRelativeTime(lastUpdatedAt)}
            </span>
          )}
          {!belowControlsSlot && (
            <button
              onClick={() => { Promise.resolve(onRefresh()).catch(() => {}); }}
              disabled={isLoading}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                isDarkMode
                  ? "bg-blue-600 hover:bg-blue-500 text-white disabled:bg-blue-800 disabled:opacity-60"
                  : "bg-blue-500 hover:bg-blue-600 text-white disabled:bg-blue-300 disabled:opacity-60"
              } ${isLoading ? "" : "hover:shadow-md active:scale-95"}`}
              title="Refresh data"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              <span>Refresh</span>
            </button>
          )}
        </div>
      </div>

      {/* Action bar: tab toggle + refresh (when belowControlsSlot is provided) */}
      {belowControlsSlot && (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          {belowControlsSlot}
          <button
            onClick={() => { Promise.resolve(onRefresh()).catch(() => {}); }}
            disabled={isLoading}
            className={`ml-auto flex items-center gap-2 sm:px-4 px-2 py-2 rounded-md text-sm font-medium transition-all ${
              isDarkMode
                ? "bg-blue-600 hover:bg-blue-500 text-white disabled:bg-blue-800 disabled:opacity-60"
                : "bg-blue-500 hover:bg-blue-600 text-white disabled:bg-blue-300 disabled:opacity-60"
            } ${isLoading ? "" : "hover:shadow-md active:scale-95"}`}
            title="Refresh data"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      )}

      {/* Table scroll container */}
      <div
        className={[
          "flex-1 mx-1 sm:mx-3 overflow-auto",
          tableContainerClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <table className={`min-w-full border-collapse text-[10px] sm:text-xs ${isDarkMode ? "bg-black" : "bg-white"}`}>
          <TableHeader
            columns={activeColumns}
            sortConfig={sortConfig}
            onSort={handleSort}
            columnFilters={columnFilters}
            onToggleFilter={handleToggleFilter}
            onSelectAllFilter={handleSelectAllFilter}
            onClearFilter={handleClearFilter}
            filterOptions={filterOptions}
            isDarkMode={isDarkMode}
            columnGroups={config.columnGroups}
            onVisibleColumnsChange={handleVisibleColumnsChange}
          />
          {isLoading ? renderLoadingSkeleton() : sortedRows.length === 0 ? renderEmptyState() : renderTableBody()}
        </table>
      </div>
    </div>
  );
};

export default PortfolioTable;
